import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import PrivateWebsiteReleases from "@/components/dashboard/PrivateWebsiteReleases";

const SITE_LANGUAGES = [
  { code: "en", name: "English" }, { code: "nl", name: "Nederlands" },
  { code: "es", name: "Español" }, { code: "de", name: "Deutsch" },
  { code: "fr", name: "Français" },
];

const LEGACY_TEMPLATES = [
  { id: "clean-business", name: "Clean Business", accent: "#17365d", surface: "#f8fafc", text: "#172b4d", layout: "business" },
  { id: "modern-studio", name: "Modern Studio", accent: "#876d46", surface: "#faf7f2", text: "#342b25", layout: "studio" },
  { id: "local-services", name: "Local Services", accent: "#285b4b", surface: "#f1f7f3", text: "#1d4035", layout: "services" },
];

function requestErrorMessage(error, fallback) {
  const status = error.response?.status;
  if (status === 413) return fallback + " The upload or draft is too large for the server.";
  if (![400, 422].includes(status)) return fallback;
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return fallback + " " + detail;
  if (Array.isArray(detail)) {
    const messages = detail.slice(0, 3).map((item) => {
      const field = Array.isArray(item.loc)
        ? item.loc.filter((part) => part !== "body").join(" → ").replaceAll("_", " ")
        : "";
      return typeof item.msg === "string" ? (field ? field + ": " : "") + item.msg : "";
    }).filter(Boolean);
    if (messages.length) return fallback + " " + messages.join("; ");
  }
  return fallback;
}

function PrivateImage({ assetId, alt }) {
  const [src, setSrc] = useState("");
  const [loading, setLoading] = useState(!!assetId);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    let objectUrl = "";
    setSrc("");
    setLoading(!!assetId);
    if (assetId) {
      axios.get(`${API}/files/${encodeURIComponent(assetId)}/download`, { responseType: "blob" })
        .then(({ data }) => {
          if (!active) return;
          objectUrl = URL.createObjectURL(data);
          setSrc(objectUrl);
        })
        .catch(() => { if (active) setSrc(""); })
        .finally(() => { if (active) setLoading(false); });
    }
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [assetId, retry]);
  if (loading) return <div role="status" className="rounded border border-white p-6">Loading photo…</div>;
  if (src) return <img src={src} alt={alt || ""} onError={() => setSrc("")}
    className="mx-auto max-h-80 rounded object-contain" />;
  return (
    <div className="space-y-2 rounded border border-white p-6">
      <p role="status">Photo preview unavailable. Your photo selection has been kept.</p>
      {assetId && <button type="button" onClick={() => { setLoading(true); setRetry((value) => value + 1); }}
        className="rounded border px-3 py-1 text-sm">Retry photo preview</button>}
    </div>
  );
}

function ExternalImage({ url, alt }) {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  return (
    <div>
      {loading && <p role="status" className="p-3 text-sm">Loading photo…</p>}
      {failed ? (
        <div className="space-y-2 rounded border border-white p-6">
          <p role="status">External photo preview unavailable. Check the image URL or try again.</p>
          <button type="button" onClick={() => { setLoading(true); setFailed(false); }}
            className="rounded border px-3 py-1 text-sm">Retry photo preview</button>
        </div>
      ) : (
        <img src={url} alt={alt || ""} onLoad={() => setLoading(false)}
          onError={() => { setLoading(false); setFailed(true); }}
          className="mx-auto max-h-80 rounded object-contain" />
      )}
    </div>
  );
}

function groupTemplateSections(sections) {
  const groups = [];
  sections.forEach((section) => {
    if (section.type === "heading" || !groups.length) groups.push([]);
    groups[groups.length - 1].push(section);
  });
  return groups;
}

export default function WebsiteBuilderPage() {
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState("");
  const [templateSearch, setTemplateSearch] = useState("");
  const [templateCategory, setTemplateCategory] = useState("");
  const TEMPLATES = catalog?.templates || LEGACY_TEMPLATES;
  const filteredTemplates = TEMPLATES.filter((item) =>
    (!templateCategory || item.category === templateCategory) &&
    item.name.toLowerCase().includes(templateSearch.toLowerCase()));
  const loadCatalog = async () => {
    try {
      const { data } = await axios.get(`${API}/website-builder/templates`);
      setCatalog(data); setCatalogError("");
    } catch {
      setCatalogError("Could not load template access. Existing drafts are unchanged.");
    }
  };
  useEffect(() => {
    const session = new URLSearchParams(window.location.search).get("template_checkout");
    if (session) {
      axios.post(`${API}/website-builder/templates/checkout/${encodeURIComponent(session)}/verify`)
        .then(async ({ data }) => {
          await loadCatalog();
          if (!data.active) setCatalogError("Template payment has not been confirmed yet.");
        })
        .catch(() => setCatalogError("Could not verify the template payment. Please retry before purchasing again."));
    } else loadCatalog();
  }, []);
  const upgradeTemplates = async () => {
    if (hasUnsavedChanges) {
      setError("Save your website changes before opening checkout."); return;
    }
    if (!window.confirm("Open secure checkout for the full 250-template library at €4.99 per month? Your main plan will not change.")) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/website-builder/templates/checkout`);
      const destination = new URL(data.url);
      if (destination.protocol !== "https:" || destination.hostname !== "checkout.stripe.com") throw new Error("Invalid checkout URL");
      window.location.assign(destination.href);
    } catch (error) {
      setError(error.response?.data?.detail || "Could not open template checkout.");
    } finally { setBusy(false); }
  };
  const cancelTemplateUpgrade = async () => {
    if (!window.confirm("Cancel only the template-library add-on at the end of its current billing period? Existing published sites are not removed.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/website-builder/templates/cancel`);
      setNotice("Template add-on cancellation scheduled for the end of the current billing period.");
    } catch { setError("Could not confirm cancellation. Contact support before assuming billing has stopped."); }
    finally { setBusy(false); }
  };
  const [drafts, setDrafts] = useState([]);
  const [draftsError, setDraftsError] = useState("");
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [template, setTemplate] = useState("consulting-overview");
  const [title, setTitle] = useState("");
  const [defaultLanguage, setDefaultLanguage] = useState("en");
  const [description, setDescription] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [sections, setSections] = useState([]);
  const [pages, setPages] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [activePage, setActivePage] = useState(null);
  const [libraryImages, setLibraryImages] = useState([]);
  const [libraryError, setLibraryError] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [previewLanguage, setPreviewLanguage] = useState("primary");
  const [previewLinks, setPreviewLinks] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [starterLayoutApplied, setStarterLayoutApplied] = useState(false);
  const snapshot = (site) => JSON.stringify({ title: site.title, description: site.description, seoTitle: site.seoTitle || "", seoDescription: site.seoDescription || "", template: site.template, defaultLanguage: site.defaultLanguage, sections: site.sections, pages: site.pages, translations: site.translations });
  const currentSnapshot = snapshot({ title, description, seoTitle, seoDescription, template, defaultLanguage, sections, pages, translations });
  const emptyDraftSnapshot = snapshot({ title: "", description: "", template: "consulting-overview", defaultLanguage: "en", sections: [], pages: [], translations: [] });
  const hasUnsavedChanges = currentSnapshot !== (savedSnapshot ?? emptyDraftSnapshot);
  const confirmDiscard = () => !hasUnsavedChanges || window.confirm("Discard unsaved website changes?");
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const warnBeforeLeave = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeave);
    return () => window.removeEventListener("beforeunload", warnBeforeLeave);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!hasUnsavedChanges && !busy) return undefined;
    const confirmLinkNavigation = (event) => {
      if (event.defaultPrevented || event.button !== 0 ||
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target?.closest?.("a[href]");
      if (!link || link.hasAttribute("download") ||
          (link.target && link.target !== "_self")) return;
      const destination = new URL(link.href, window.location.href);
      if (!["http:", "https:"].includes(destination.protocol)) return;
      if (destination.origin === window.location.origin &&
          destination.pathname === window.location.pathname &&
          destination.search === window.location.search) return;
      if (busy || !window.confirm("Discard unsaved website changes and leave the editor?")) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (busy) setNotice("Please wait until the current website operation finishes before leaving the editor.");
      }
    };
    document.addEventListener("click", confirmLinkNavigation, true);
    return () => document.removeEventListener("click", confirmLinkNavigation, true);
  }, [hasUnsavedChanges, busy]);

  const load = async () => {
    setDraftsLoading(true);
    try {
      const { data } = await axios.get(`${API}/website-builder/drafts`);
      setDrafts(data.drafts || []);
      setDraftsError("");
    } catch (error) {
      setDraftsError("Could not refresh saved drafts. Your current website content has been kept.");
      throw error;
    } finally { setDraftsLoading(false); }
  };
  const retryDrafts = async () => {
    if (busy || draftsLoading) return;
    setBusy(true);
    try { await load(); }
    catch { /* load displays the draft list error. */ }
    finally { setBusy(false); }
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const internalPageLinks = [
    { path: "/", label: defaultLanguage.toUpperCase() + " · Home", pageId: null, language: "primary" },
    ...pages.map((page) => ({ path: "/" + page.slug + "/", label: defaultLanguage.toUpperCase() + " · " + page.title, pageId: page.id, language: "primary" })),
    ...translations.flatMap((translation) => [
      { path: "/" + translation.language + "/", label: translation.language.toUpperCase() + " · Home", pageId: null, language: translation.language },
      ...translation.pages.map((page, index) => ({
        path: "/" + translation.language + "/" + page.slug + "/",
        label: translation.language.toUpperCase() + " · " + page.title,
        pageId: pages[index]?.id, language: translation.language,
      })),
    ]),
  ];
  const followInternalLink = (path) => {
    const target = internalPageLinks.find((item) => item.path === path);
    if (!target || target.pageId === undefined) {
      setError("This button points to a page that no longer exists. Update its destination.");
      return;
    }
    setPreviewLanguage(target.language); setActivePage(target.pageId); setMobileMenuOpen(false);
  };

  const isInvalidUrl = (value, allowInternal = false) => {
    if (!value) return false;
    if (/\s/.test(value)) return true;
    if (/^mailto:/i.test(value)) return !allowInternal ||
      !/^mailto:[A-Za-z0-9_+-]+(?:\.[A-Za-z0-9_+-]+)*@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/i.test(value);
    if (/^tel:/i.test(value)) return !allowInternal || !/^tel:\+?[0-9]{3,15}$/i.test(value);
    if (value.startsWith("/")) return !allowInternal || !internalPageLinks.some((item) => item.path === value);
    try {
      const parsed = new URL(value);
      return !["http:", "https:"].includes(parsed.protocol) || !parsed.hostname ||
        !!parsed.username || !!parsed.password || /\s/.test(value);
    } catch { return true; }
  };
  const languageFolderConflicts = pages.filter((page) => translations.some((translation) => translation.language === page.slug));
  const contentWarnings = [
    ...languageFolderConflicts.map((page) => `Page "/${page.slug}/" conflicts with an additional language. Change the page URL slug.`),
    ...(!title.trim() ? ["Add a website title."] : []),
    ...(title.trim().length > 160 ? ["Website title must be at most 160 characters."] : []),
    ...(!description.trim() ? ["Add a business description for the home page."] : []),
    ...(hasUnsavedChanges ? ["Save your latest changes before leaving the editor."] : []),
    ...(!sections.length ? ["The home page has no content sections."] : []),
    ...(pages.some((page) => !page.title.trim()) ? ["Every page needs a title."] : []),
    ...pages.filter((page) => page.title.length > 160).map((page) => `Page "${page.slug}" has a title longer than 160 characters.`),
    ...(pages.some((page) => page.slug.length > 100 || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(page.slug)) ? ["Page URL slugs must contain only lowercase letters, numbers and single hyphens."] : []),
    ...(new Set(pages.map((page) => page.slug)).size !== pages.length ? ["Page URL slugs must be unique."] : []),
    ...pages.filter((page) => !page.sections.length).map((page) => `Page "${page.title || page.slug}" has no sections.`),
    ...(new Set(translations.map((translation) => translation.language)).size !== translations.length
      ? ["Additional website languages must be unique."] : []),
    ...(translations.some((translation) => translation.language === defaultLanguage)
      ? ["An additional language cannot match the primary website language."] : []),
    ...translations.flatMap((translation) => {
      const label = translation.language.toUpperCase();
      const warnings = [];
      if (!translation.title.trim()) warnings.push(`${label}: Website title is missing.`);
      if (translation.title.length > 160) warnings.push(`${label}: Website title must be at most 160 characters.`);
      if (!translation.description.trim()) warnings.push(`${label}: Website description is missing.`);
      if (!translation.sections.length) warnings.push(`${label}: Home page has no sections.`);
      if (translation.pages.length !== pages.length) warnings.push(`${label}: Page count differs from the primary website.`);
      const slugs = translation.pages.map((page) => page.slug);
      if (new Set(slugs).size !== slugs.length) warnings.push(`${label}: Page URL slugs must be unique.`);
      for (const page of translation.pages) {
        if (!page.title.trim()) warnings.push(`${label}: A page has no title.`);
        if (page.title.length > 160) warnings.push(`${label}: Page "${page.slug}" has a title longer than 160 characters.`);
        if (page.slug.length > 100 || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(page.slug)) warnings.push(`${label}: A page has an invalid URL slug (maximum 100 characters).`);
        if (!page.sections.length) warnings.push(`${label}: Page "${page.title || page.slug}" has no sections.`);
      }
      for (const section of [...translation.sections, ...translation.pages.flatMap((page) => page.sections)]) {
        if (["heading", "text", "button"].includes(section.type) && !section.text.trim()) warnings.push(`${label}: A content section has no text.`);
        if (section.type === "button" && !section.url.trim()) warnings.push(`${label}: A button has no destination URL.`);
        if (isInvalidUrl(section.url, section.type === "button")) warnings.push(`${label}: A link or image URL is invalid.`);
        if (section.type === "image" && !section.asset_id && !section.url.trim()) warnings.push(`${label}: An image section has no photo.`);
        if (section.type === "image" && (section.asset_id || section.url.trim()) && !section.text.trim()) warnings.push(`${label}: A photo has no image description. Add one if the photo conveys information.`);
        if (section.type === "image" && section.asset_id && section.url.trim()) warnings.push(`${label}: Choose one image source (library or URL).`);
      }
      return warnings;
    }),
    ...[...sections, ...pages.flatMap((page) => page.sections)].flatMap((section) => {
      const warnings = [];
      if (["heading", "text", "button"].includes(section.type) && !section.text.trim()) warnings.push("A content section has no text.");
      if (section.type === "button" && !section.url.trim()) warnings.push(`Button "${section.text || "Untitled"}" has no destination URL.`);
      if (isInvalidUrl(section.url, section.type === "button")) warnings.push("Use HTTP(S) for images. Buttons also support existing page paths, mailto:email@example.com and tel:+34123456789.");
      if (section.type === "image" && !section.asset_id && !section.url.trim()) warnings.push("An image section has no photo.");
      if (section.type === "image" && (section.asset_id || section.url.trim()) && !section.text.trim()) warnings.push("A photo has no image description. Add one if the photo conveys information.");
      if (section.type === "image" && section.asset_id && section.url.trim()) warnings.push("Choose one image source (library or URL).");
      return warnings;
    }),
  ];
  const uniqueContentWarnings = [...new Set(contentWarnings)];
  const validateSectionUrls = () => {
    if ([...sections, ...pages.flatMap((page) => page.sections)].some((section) => isInvalidUrl(section.url, section.type === "button"))) {
      setError("A destination is invalid. Images need HTTP(S); buttons also accept existing page paths, mailto:email@example.com or tel:+34123456789.");
      return false;
    }
    if ([...sections, ...pages.flatMap((page) => page.sections)].some((section) => section.type === "image" && section.asset_id && section.url?.trim())) {
      setError("Choose either a library photo or an external URL for each image.");
      return false;
    }
    return true;
  };
  const updateTranslatedSection = (language, pageId, sectionId, changes) => {
    setTranslations((items) => items.map((item) => {
      if (item.language !== language) return item;
      const update = (parts) => parts.map((part) => part.id === sectionId ? { ...part, ...changes } : part);
      return pageId === "home" ? { ...item, sections: update(item.sections) } : {
        ...item, pages: item.pages.map((page) => page.id === pageId ? { ...page, sections: update(page.sections) } : page),
      };
    }));
  };
  const addTranslatedSection = (language, pageId, type) => {
    setTranslations((items) => items.map((item) => {
      if (item.language !== language) return item;
      const append = (parts) => parts.length >= 100 ? parts : [...parts, {
        id: crypto.randomUUID(), type, text: "", url: "",
      }];
      return pageId === "home" ? { ...item, sections: append(item.sections) } : {
        ...item, pages: item.pages.map((page) => page.id === pageId ? { ...page, sections: append(page.sections) } : page),
      };
    }));
  };
  const removeTranslatedSection = (language, pageId, sectionId) => {
    if (!window.confirm("Remove this translated section?")) return;
    setTranslations((items) => items.map((item) => {
      if (item.language !== language) return item;
      const remove = (parts) => parts.filter((part) => part.id !== sectionId);
      return pageId === "home" ? { ...item, sections: remove(item.sections) } : {
        ...item, pages: item.pages.map((page) => page.id === pageId ? { ...page, sections: remove(page.sections) } : page),
      };
    }));
  };
  const moveTranslatedSection = (language, pageId, sectionId, direction) => {
    setTranslations((items) => items.map((item) => {
      if (item.language !== language) return item;
      const move = (parts) => {
        const index = parts.findIndex((part) => part.id === sectionId);
        const target = index + direction;
        if (index < 0 || target < 0 || target >= parts.length) return parts;
        const next = [...parts];
        [next[index], next[target]] = [next[target], next[index]];
        return next;
      };
      return pageId === "home" ? { ...item, sections: move(item.sections) } : {
        ...item, pages: item.pages.map((page) => page.id === pageId ? { ...page, sections: move(page.sections) } : page),
      };
    }));
  };
  const duplicateTranslatedSection = (language, pageId, sectionId) => {
    setTranslations((items) => items.map((item) => {
      if (item.language !== language) return item;
      const duplicate = (parts) => {
        if (parts.length >= 100) return parts;
        const index = parts.findIndex((part) => part.id === sectionId);
        if (index < 0) return parts;
        return [...parts.slice(0, index + 1),
          { ...parts[index], id: crypto.randomUUID() }, ...parts.slice(index + 1)];
      };
      return pageId === "home" ? { ...item, sections: duplicate(item.sections) } : {
        ...item, pages: item.pages.map((page) => page.id === pageId ? { ...page, sections: duplicate(page.sections) } : page),
      };
    }));
  };
  const addLanguageCopy = (language) => {
    if (busy || language === defaultLanguage ||
        !SITE_LANGUAGES.some((item) => item.code === language)) return;
    if (pages.some((page) => page.slug === language)) {
      setError("A page already uses /" + language + "/. Change that page URL before adding this language.");
      return;
    }
    const primaryPaths = new Set(["/", ...pages.map((page) => "/" + page.slug + "/")]);
    const copySection = (section) => ({
      ...section, id: crypto.randomUUID(),
      url: section.type === "button" && primaryPaths.has(section.url)
        ? "/" + language + section.url : section.url,
    });
    const copy = {
      language, title: title.trim() || "New website",
      seo_title: seoTitle, seo_description: seoDescription, description,
      sections: sections.map(copySection),
      pages: pages.map((page) => ({
        ...page, id: crypto.randomUUID(), sections: page.sections.map(copySection),
      })),
    };
    setTranslations((items) => items.length >= 4 || items.some((item) => item.language === language)
      ? items : [...items, copy]);
    setError("");
  };

  const validateTranslations = () => {
    const languages = translations.map((translation) => translation.language);
    if (new Set(languages).size !== languages.length || translations.length > 4) {
      setError("Each additional language must be unique (maximum four).");
      return false;
    }
    const validSlug = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    for (const translation of translations) {
      if (!translation.title.trim() || translation.language === defaultLanguage || !SITE_LANGUAGES.some((item) => item.code === translation.language)) {
        setError("Each translation needs a title and a language different from the primary language.");
        return false;
      }
      if (translation.title.length > 160 || translation.description.length > 2000) {
        setError(`The ${translation.language.toUpperCase()} website title must be at most 160 characters and its description at most 2000 characters.`);
        return false;
      }
      if ((translation.seo_title || "").length > 160 || (translation.seo_description || "").length > 320) {
        setError(`Check the search result title and description lengths for ${translation.language.toUpperCase()}.`);
        return false;
      }
      if (translation.pages.length !== pages.length) {
        setError(`The ${translation.language.toUpperCase()} page count differs from the primary site. Align the pages before saving.`);
        return false;
      }
      const slugs = translation.pages.map((page) => page.slug);
      if (translation.pages.some((page) => !page.title.trim() || page.title.length > 160 || page.slug.length > 100 || !validSlug.test(page.slug)) ||
          new Set(slugs).size !== slugs.length) {
        setError(`Check page titles and unique lowercase URL slugs for ${translation.language.toUpperCase()}.`);
        return false;
      }
      if ([...translation.sections, ...translation.pages.flatMap((page) => page.sections)]
        .some((section) => isInvalidUrl(section.url, section.type === "button"))) {
        setError(`A link in the ${translation.language.toUpperCase()} translation has an invalid URL.`);
        return false;
      }
      if ([...translation.sections, ...translation.pages.flatMap((page) => page.sections)]
        .some((section) => section.type === "image" && section.asset_id && section.url?.trim())) {
        setError(`Choose one image source for each ${translation.language.toUpperCase()} image (library or URL).`);
        return false;
      }
    }
    return true;
  };
  const validatePages = () => {
    if (languageFolderConflicts.length) {
      setError(`Page "/${languageFolderConflicts[0].slug}/" conflicts with an additional language. Choose a different page URL slug before saving.`);
      return false;
    }
    const slugs = pages.map((page) => page.slug);
    if (pages.some((page) => !page.title.trim() || page.title.length > 160 || page.slug.length > 100 || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(page.slug))) {
      setError("Each page needs a title and a lowercase URL slug (letters, numbers, hyphens).");
      return false;
    }
    if (new Set(slugs).size !== slugs.length) {
      setError("Page URL slugs must be unique."); return false;
    }
    return true;
  };

  const validateWebsiteDetails = () => {
    if (!title.trim()) {
      setError("Enter a website title.");
      return false;
    }
    if (title.trim().length > 160) {
      setError("Website title must be at most 160 characters.");
      return false;
    }
    if (seoTitle.length > 160 || seoDescription.length > 320) {
      setError("Search result title must be at most 160 characters and its description at most 320 characters.");
      return false;
    }
    if (description.length > 2000) {
      setError("Website description must be at most 2000 characters.");
      return false;
    }
    return true;
  };

  const create = async () => {
    if (!validateWebsiteDetails()) return;
    if (!validatePages() || !validateSectionUrls() || !validateTranslations()) return;
    if (translations.some((translation) => !translation.title.trim() || translation.language === defaultLanguage)) return setError("Translated sites need a title and a language different from the primary language.");
    setBusy(true); setError(""); setNotice("");
    try {
      const { data } = await axios.post(`${API}/website-builder/drafts`, {
        title: title.trim(), description, seo_title: seoTitle, seo_description: seoDescription, template_id: template, default_language: defaultLanguage, sections, pages, translations
      });
      setDrafts((items) => [data, ...items.filter((draft) => draft.id !== data.id)].slice(0, 100));
      setSelected(data); setStarterLayoutApplied(false); setPreviewLanguage("primary"); setMobileMenuOpen(false); setTemplate(data.template_id); setTitle(data.title); setSeoTitle(data.seo_title || ""); setSeoDescription(data.seo_description || ""); setDescription(data.description || ""); setDefaultLanguage(data.default_language || "en"); setSections(data.sections || []); setPages(data.pages || []); setTranslations(data.translations || []); setActivePage(null);
      setSavedSnapshot(snapshot({ title: data.title, description: data.description || "", template: data.template_id, defaultLanguage: data.default_language || "en", sections: data.sections || [], pages: data.pages || [], translations: data.translations || [], seoTitle: data.seo_title || "", seoDescription: data.seo_description || "" })); await load().catch(() => setNotice("Draft created and saved. Refresh the list to see the latest drafts.")); setNotice((current) => current || "Draft created and saved.");
    } catch (error) { setError(requestErrorMessage(error, "Could not create the draft. Your edits are still here.")); }
    finally { setBusy(false); }
  };

  const open = async (listedDraft) => {
    if (busy || !confirmDiscard()) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const { data: draft } = await axios.get(`${API}/website-builder/drafts/${encodeURIComponent(listedDraft.id)}`);
      setSavedSnapshot(snapshot({ title: draft.title, description: draft.description || "", template: draft.template_id, defaultLanguage: draft.default_language || "en", sections: draft.sections || [], pages: draft.pages || [], translations: draft.translations || [], seoTitle: draft.seo_title || "", seoDescription: draft.seo_description || "" }));
      setSelected(draft); setStarterLayoutApplied(false); setPreviewLanguage("primary"); setMobileMenuOpen(false); setTitle(draft.title); setSeoTitle(draft.seo_title || ""); setSeoDescription(draft.seo_description || ""); setDescription(draft.description || "");
      setTemplate(draft.template_id); setDefaultLanguage(draft.default_language || "en"); setSections(draft.sections || []); setPages(draft.pages || []); setTranslations(draft.translations || []); setActivePage(null); setError(""); setNotice("");
      setDrafts((items) => items.map((item) => item.id === draft.id ? draft : item));
    } catch (error) {
      if (error.response?.status === 404) {
        setDrafts((items) => items.filter((item) => item.id !== listedDraft.id));
        setError("This draft is no longer available. Your current editor content has been kept.");
      } else {
        setError("Could not open the latest draft. Your current editor content has been kept. Try opening it again.");
      }
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!selected) return;
    if (!validateWebsiteDetails()) return;
    if (!validatePages() || !validateSectionUrls() || !validateTranslations()) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const { data } = await axios.patch(`${API}/website-builder/drafts/${encodeURIComponent(selected.id)}`, {
        title: title.trim(), description, seo_title: seoTitle, seo_description: seoDescription, template_id: template, default_language: defaultLanguage, sections, pages, translations,
        ...(selected.updated_at ? { expected_updated_at: selected.updated_at } : {})
      });
      setDrafts((items) => [data, ...items.filter((draft) => draft.id !== data.id)].slice(0, 100));
      setSelected(data); setTitle(data.title); setSeoTitle(data.seo_title || ""); setSeoDescription(data.seo_description || ""); setDescription(data.description || ""); setTemplate(data.template_id); setDefaultLanguage(data.default_language || "en"); setSections(data.sections || []); setPages(data.pages || []); setTranslations(data.translations || []);
      setSavedSnapshot(snapshot({ title: data.title, description: data.description || "", template: data.template_id, defaultLanguage: data.default_language || "en", sections: data.sections || [], pages: data.pages || [], translations: data.translations || [], seoTitle: data.seo_title || "", seoDescription: data.seo_description || "" })); await load().catch(() => setNotice("Draft saved. Refresh the list to see the latest drafts.")); setNotice((current) => current || "Draft saved.");
    } catch (error) {
      setError(error.response?.status === 409
        ? "This draft was saved elsewhere after you opened it. Your edits are still here. Copy any text you want to keep before reopening the latest version."
        : requestErrorMessage(error, "Could not save the draft. Your edits are still here."));
    }
    finally { setBusy(false); }
  };

  const downloadPublication = async () => {
    if (busy || !selected) return;
    if (hasUnsavedChanges) {
      setError("Save your changes before downloading the website package.");
      return;
    }
    setBusy(true); setDownloading(true); setError(""); setNotice("");
    let objectUrl = "";
    try {
      const { data } = await axios.post(
        `${API}/website-builder/drafts/${encodeURIComponent(selected.id)}/download-publication`,
        null, {
          responseType: "blob",
          params: selected.updated_at ? { expected_updated_at: selected.updated_at } : {},
        }
      );
      if (!(data instanceof Blob) || !data.size) throw new Error("Empty website package");
      objectUrl = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = "zynthoro-website.zip";
      document.body.appendChild(link);
      try { link.click(); }
      finally { link.remove(); }
      setNotice("Website package download started for the saved version shown in this editor. Library photos are included; external photos remain linked. Nothing has been published.");
    } catch (error) {
      if (error.response?.data instanceof Blob && error.response.data.size < 65536) {
        try {
          error.response.data = JSON.parse(await error.response.data.text());
        } catch { /* Keep the fallback if the response is not JSON. */ }
      }
      setError(error.response?.status === 409
        ? "This draft changed elsewhere. Reopen the latest version before downloading. Your editor content has been kept and nothing has been published."
        : requestErrorMessage(error, "Could not prepare the website package. Your draft has been kept and nothing has been published."));
    } finally {
      setBusy(false); setDownloading(false);
      if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    }
  };

  const remove = async () => {
    if (!selected || !window.confirm("Delete this website draft?")) return;
    setBusy(true); setError(""); setNotice("");
    try {
      await axios.delete(`${API}/website-builder/drafts/${encodeURIComponent(selected.id)}`, {
        params: selected.updated_at ? { expected_updated_at: selected.updated_at } : {},
      });
      setDrafts((items) => items.filter((draft) => draft.id !== selected.id));
      setSelected(null); setStarterLayoutApplied(false); setPreviewLanguage("primary"); setMobileMenuOpen(false); setTitle(""); setSeoTitle(""); setSeoDescription(""); setDescription(""); setSections([]); setPages([]); setTranslations([]); setActivePage(null); setTemplate("consulting-overview"); setDefaultLanguage("en"); setSavedSnapshot(null); await load().catch(() => setNotice("Draft deleted. Refresh the list to see the latest drafts."));
      setNotice((current) => current || "Draft deleted.");
    } catch (error) {
      const detail = error.response?.data?.detail;
      setError(typeof detail === "string" ? detail + " Your current editor content has been kept."
        : requestErrorMessage(error, "Could not delete the draft. Your current editor content has been kept."));
    }
    finally { setBusy(false); }
  };

  const loadImages = async () => {
    try {
      const { data } = await axios.get(`${API}/files`, { params: { kind: "brand_asset" } });
      setLibraryImages(data.files || []);
      setLibraryError("");
    } catch (error) {
      setLibraryError("The photo library could not load. Your website content has been kept.");
      throw error;
    }
  };
  const retryImages = async () => {
    if (busy) return;
    setBusy(true);
    try { await loadImages(); }
    catch { /* loadImages displays the library error. */ }
    finally { setBusy(false); }
  };
  useEffect(() => { loadImages().catch(() => {}); }, []);

  const uploadImage = async (file, sectionId) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Use a JPEG, PNG or WebP image."); return;
    }
    setBusy(true); setError("");
    try {
      const body = new FormData();
      body.append("kind", "brand_asset");
      body.append("file", file);
      const { data } = await axios.post(`${API}/files`, body);
      editSection(sectionId, { asset_id: data.id, url: "" });
      const libraryRefreshed = await loadImages().then(() => true, () => false);
      setNotice(libraryRefreshed
        ? "Photo uploaded. Save changes to keep it in your draft."
        : "Photo uploaded and added to this section. The photo library could not refresh. Save changes to keep the photo in your draft.");
    } catch (error) { setError(requestErrorMessage(error, "Could not upload photo.")); }
    finally { setBusy(false); }
  };

  const uploadTranslatedImage = async (file, language, pageId, sectionId) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Use a JPEG, PNG or WebP image."); return;
    }
    setBusy(true); setError("");
    try {
      const body = new FormData();
      body.append("kind", "brand_asset");
      body.append("file", file);
      const { data } = await axios.post(`${API}/files`, body);
      updateTranslatedSection(language, pageId, sectionId, { asset_id: data.id, url: "" });
      const libraryRefreshed = await loadImages().then(() => true, () => false);
      setNotice(libraryRefreshed
        ? "Translated photo uploaded. Save changes to keep it in your draft."
        : "Translated photo uploaded and added to this section. The photo library could not refresh. Save changes to keep the photo in your draft.");
    } catch (error) { setError(requestErrorMessage(error, "Could not upload translated photo.")); }
    finally { setBusy(false); }
  };

  const addPage = () => {
    if (pages.length >= 30) return;
    const id = crypto.randomUUID();
    const slug = `page-${pages.length + 1}-${id.slice(0, 6)}`;
    setPages((items) => [...items, { id, title: "New page", description: "", slug, sections: [] }]);
    setTranslations((items) => items.map((item) => {
      let translatedSlug = slug;
      let suffix = 2;
      while (item.pages.some((page) => page.slug === translatedSlug)) {
        translatedSlug = `${slug}-${suffix++}`;
      }
      return { ...item, pages: [...item.pages, { id: crypto.randomUUID(), title: "New page", description: "", slug: translatedSlug, sections: [] }] };
    }));
    setActivePage(id);
  };
  const updatePage = (id, patch) => setPages((items) =>
    items.map((page) => page.id === id ? { ...page, ...patch } : page));
  const duplicatePage = (id) => {
    if (pages.length >= 30) return;
    const original = pages.find((page) => page.id === id);
    if (!original) return;
    const newId = crypto.randomUUID();
    const slugBase = (original.slug || "page").slice(0, 75);
    let slug = `${slugBase}-copy`;
    let suffix = 2;
    while (pages.some((page) => page.slug === slug)) {
      slug = `${slugBase}-copy-${suffix++}`;
    }
    setPages((items) => [...items, {
      ...original, id: newId, title: `${original.title.slice(0, 150)} Copy`,
      slug, sections: original.sections.map((section) => ({ ...section, id: crypto.randomUUID() })),
    }]);
    setTranslations((items) => items.map((item) => {
      const source = item.pages[pages.findIndex((page) => page.id === id)];
      if (!source) return item;
      const base = (source.slug || "page").slice(0, 75);
      let slugCopy = base + "-copy";
      let suffix = 2;
      while (item.pages.some((page) => page.slug === slugCopy)) slugCopy = base + "-copy-" + suffix++;
      return { ...item, pages: [...item.pages, { ...source, id: crypto.randomUUID(), title: source.title.slice(0, 150) + " Copy", slug: slugCopy, sections: source.sections.map((section) => ({ ...section, id: crypto.randomUUID() })) }] };
    }));
    setActivePage(newId);
  };
  const removePage = (id) => {
    if (!window.confirm("Remove this page and all its sections? Save the draft to confirm the change.")) return;
    const index = pages.findIndex((page) => page.id === id);
    setPages((items) => items.filter((page) => page.id !== id));
    if (index >= 0) setTranslations((items) => items.map((item) => ({ ...item, pages: item.pages.filter((_, pageIndex) => pageIndex !== index) })));
    setActivePage(null);
  };
  const visibleSections = activePage
    ? (pages.find((page) => page.id === activePage)?.sections || []) : sections;
  const setVisibleSections = (updater) => {
    if (!activePage) return setSections(updater);
    setPages((items) => items.map((page) => page.id === activePage
      ? { ...page, sections: typeof updater === "function" ? updater(page.sections) : updater }
      : page));
  };

  const addSection = (type) => setVisibleSections((items) => items.length >= 100 ? items : [...items, {
    id: crypto.randomUUID(), type, text: "", url: ""
  }]);
  const addStarterLayout = () => {
    if (!TEMPLATES.some((item) => item.id === template)) {
      setError("Choose an available template before applying a starter layout. Your existing content has been kept.");
      return;
    }
    if (visibleSections.length && !window.confirm("Replace the current page sections with the starter layout?")) return;
    const chosen = TEMPLATES.find((item) => item.id === template);
    if (!catalog || !chosen?.available || !chosen.sections) {
      setError("Load the catalog and choose a template included in your library. Existing content has been kept.");
      return;
    }
    setVisibleSections(chosen.sections.map((item) => ({ ...item, id: crypto.randomUUID() })));
    setStarterLayoutApplied(true);
    setNotice("Starter layout added. Replace sample text and add your own links before publishing.");
  };
  const editSection = (id, patch) => setVisibleSections((items) =>
    items.map((item) => item.id === id ? { ...item, ...patch } : item));
  const duplicateSection = (id) => setVisibleSections((items) => {
    if (items.length >= 100) return items;
    const index = items.findIndex((item) => item.id === id);
    if (index < 0) return items;
    const copy = { ...items[index], id: crypto.randomUUID() };
    return [...items.slice(0, index + 1), copy, ...items.slice(index + 1)];
  });
  const removeSection = (id) => {
    if (!window.confirm("Remove this section?")) return;
    setVisibleSections((items) => items.filter((item) => item.id !== id));
  };
  const moveSection = (index, direction) => setVisibleSections((items) => {
    const next = [...items], destination = index + direction;
    if (destination < 0 || destination >= next.length) return items;
    [next[index], next[destination]] = [next[destination], next[index]];
    return next;
  });

  const activeTemplate = TEMPLATES.find((t) => t.id === template) || TEMPLATES[0];
  const previewPage = pages.find((page) => page.id === activePage);
  const previewTranslation = translations.find((item) => item.language === previewLanguage);
  const previewPageIndex = pages.findIndex((page) => page.id === activePage);
  const translatedPreviewPage = previewPageIndex >= 0 ? previewTranslation?.pages[previewPageIndex] : null;
  const previewContent = previewTranslation
    ? (previewPageIndex >= 0 ? (translatedPreviewPage?.sections || []) : previewTranslation.sections)
    : visibleSections;
  const previewNavigationPages = previewTranslation?.pages || pages;
  const previewBusinessFallback = ({ en: "Your business", nl: "Jouw bedrijf", es: "Tu empresa", de: "Dein Unternehmen", fr: "Votre entreprise" })[previewTranslation?.language || defaultLanguage] || "Your business";
  const previewTitle = previewTranslation
    ? (previewPageIndex >= 0 ? translatedPreviewPage?.title : previewTranslation.title) || previewBusinessFallback
    : previewPage?.title || title || previewBusinessFallback;
  const previewDescription = previewTranslation?.description ?? description;
  const previewPageDescription = previewTranslation ? (translatedPreviewPage?.description || "") : (previewPage?.description || "");
  const previewSeoSource = previewPageIndex >= 0
    ? (previewTranslation ? translatedPreviewPage : previewPage)
    : (previewTranslation || { seo_title: seoTitle, seo_description: seoDescription });
  const previewSeoTitle = previewSeoSource?.seo_title?.trim() || previewTitle;
  const previewSeoDescription = previewSeoSource?.seo_description?.trim() ||
    (previewPageIndex >= 0 ? previewPageDescription : previewDescription);
  const previewSeoPath = previewPageIndex >= 0 ? "/" + (previewSeoSource?.slug || "") : "/";

  const previewHomeLabel = ({ en: "Home", nl: "Start", es: "Inicio", de: "Startseite", fr: "Accueil" })[previewTranslation?.language || defaultLanguage] || "Home";
  const previewMenuLabels = ({ en: ["Menu", "Close menu"], nl: ["Menu", "Menu sluiten"], es: ["Menú", "Cerrar menú"], de: ["Menü", "Menü schließen"], fr: ["Menu", "Fermer le menu"] })[previewTranslation?.language || defaultLanguage] || ["Menu", "Close menu"];
  const previewPlaceholders = ({
    en: { emptyPage: "Add sections to build this page.", description: "Tell customers what makes your business special.", heading: "Heading", text: "Your text", button: "Button", image: "Image placeholder" },
    nl: { emptyPage: "Voeg secties toe om deze pagina op te bouwen.", description: "Vertel klanten wat jouw bedrijf bijzonder maakt.", heading: "Koptekst", text: "Jouw tekst", button: "Knop", image: "Ruimte voor een afbeelding" },
    es: { emptyPage: "Añade secciones para crear esta página.", description: "Cuenta a tus clientes qué hace especial a tu empresa.", heading: "Encabezado", text: "Tu texto", button: "Botón", image: "Espacio para una imagen" },
    de: { emptyPage: "Füge Abschnitte hinzu, um diese Seite zu gestalten.", description: "Zeige deinen Kunden, was dein Unternehmen besonders macht.", heading: "Überschrift", text: "Dein Text", button: "Schaltfläche", image: "Platz für ein Bild" },
    fr: { emptyPage: "Ajoutez des sections pour créer cette page.", description: "Expliquez à vos clients ce qui rend votre entreprise unique.", heading: "Titre", text: "Votre texte", button: "Bouton", image: "Emplacement pour une image" },
  })[previewTranslation?.language || defaultLanguage];
  const previewUntitledLabel = ({ en: "Untitled", nl: "Zonder titel", es: "Sin título", de: "Ohne Titel", fr: "Sans titre" })[previewTranslation?.language || defaultLanguage] || "Untitled";
  return (
    <div className="space-y-5" data-testid="website-builder">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Website Builder</h1>
        <p className="text-sm text-slate-600">Create and save website drafts. Publishing and domain connection are not available yet.</p>
      </div>
      {error && <p role="alert" className="text-red-700">{error}</p>}
      {notice && <p role="status" className="text-green-700">{notice}</p>}
      <datalist id="website-internal-page-links">
        {internalPageLinks.map((item, index) => <option key={`${item.path}:${index}`} value={item.path}>{item.label}</option>)}
      </datalist>
      <fieldset disabled={busy} aria-busy={busy} className="grid min-w-0 gap-5 border-0 p-0 lg:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="rounded-xl border bg-white p-4 space-y-3">
          <h2 className="font-semibold">Saved drafts</h2>
          <button type="button" className="text-sm underline" onClick={() => { if (busy || !confirmDiscard()) return; setError(""); setNotice(""); setSelected(null); setStarterLayoutApplied(false); setPreviewLanguage("primary"); setMobileMenuOpen(false); setTitle(""); setSeoTitle(""); setSeoDescription(""); setDescription(""); setSections([]); setPages([]); setTranslations([]); setActivePage(null); setTemplate("consulting-overview"); setDefaultLanguage("en"); setSavedSnapshot(null); }}>+ New website</button>
          {draftsLoading && <p role="status" className="text-sm text-slate-500">Loading drafts…</p>}
          {draftsError && (
            <div role="alert" className="space-y-2 text-sm text-amber-900">
              <p>{draftsError}</p>
              <button type="button" disabled={busy || draftsLoading} onClick={retryDrafts}
                className="rounded border px-3 py-1 disabled:opacity-50">Retry saved drafts</button>
            </div>
          )}
          {!draftsLoading && !draftsError && drafts.length === 0 && <p className="text-sm text-slate-500">No drafts yet.</p>}
          {drafts.map((draft) => (
            <button key={draft.id} type="button" onClick={() => open(draft)}
              className={`block w-full text-left rounded-lg border px-3 py-2 text-sm ${selected?.id === draft.id ? "border-slate-900" : "border-slate-200"}`}>
              {draft.title}
              <span className="block text-xs text-slate-500">Draft · {draft.template_id}</span>
            </button>
          ))}
        </aside>
        <div className="min-w-0 space-y-4">
          <section className="rounded-xl border bg-white p-5 space-y-4">
            {libraryError && (
              <div role="alert" className="rounded border border-amber-300 p-3 text-sm text-amber-900">
                <p>{libraryError}</p>
                <button type="button" disabled={busy} onClick={retryImages}
                  className="mt-2 rounded border px-3 py-1 disabled:opacity-50">Retry photo library</button>
              </div>
            )}
            <label className="block text-sm font-medium">Website title
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160}
                className="mt-1 block w-full rounded-md border p-2" placeholder="My business" />
            </label>
            <div className="space-y-2 rounded border p-3" aria-label="Homepage SEO settings">
              <p className="text-sm font-medium">Homepage search result settings</p>
              <p className="text-xs text-slate-500">Saved with this draft for publication. Separate from the visible website title and description.</p>
              <label className="block text-xs">Search result title
                <input maxLength={160} value={seoTitle} placeholder={title}
                  onChange={(e) => setSeoTitle(e.target.value)} className="mt-1 w-full rounded border p-2" />
              </label>
              <label className="block text-xs">Search result description
                <textarea maxLength={320} rows={2} value={seoDescription}
                  onChange={(e) => setSeoDescription(e.target.value)} className="mt-1 w-full rounded border p-2" />
              </label>
            </div>
            <label className="block text-sm font-medium">Website language
              <select value={defaultLanguage} onChange={(e) => {
                const next = e.target.value;
                if (translations.some((translation) => translation.language === next)) {
                  setError("Remove the existing translation for that language before selecting it as primary.");
                  return;
                }
                setError(""); setDefaultLanguage(next); setMobileMenuOpen(false);
              }}
                className="mt-1 block w-full rounded-md border p-2">
                {SITE_LANGUAGES.map((language) => <option key={language.code} value={language.code}>{language.name}</option>)}
              </select>
              <span className="mt-1 block text-xs font-normal text-slate-500">Sets the primary website language. Additional editable language copies are available below.</span>
            </label>
            <section className="space-y-3 rounded-lg border p-3" aria-label="Website translations">
              <h3 className="font-medium">Additional languages</h3>
              <p className="text-xs text-slate-600">Create editable language copies. Text is copied as a starting point, not automatically translated.</p>
              <div className="flex flex-wrap gap-2">
                {SITE_LANGUAGES.filter((language) => language.code !== defaultLanguage && !translations.some((item) => item.language === language.code)).map((language) =>
                  <button key={language.code} type="button" disabled={busy} className="rounded border px-3 py-1 text-sm"
                    onClick={() => addLanguageCopy(language.code)}>+ {language.name}</button>
                )}
              </div>
              {translations.map((translation) => (
                <div key={translation.language} className="rounded border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <strong className="text-sm">{SITE_LANGUAGES.find((item) => item.code === translation.language)?.name}</strong>
                    <button type="button" className="text-sm text-red-700" onClick={() => {
                      if (window.confirm("Remove this language copy?")) setTranslations((items) => items.filter((item) => item.language !== translation.language));
                    }}>Remove language</button>
                  </div>
                  <label className="block text-xs">Translated website title
                    <input maxLength={160} value={translation.title} className="mt-1 w-full rounded border p-2"
                      onChange={(event) => setTranslations((items) => items.map((item) => item.language === translation.language ? { ...item, title: event.target.value } : item))} />
                  </label>
                  <label className="block text-xs">Translated description
                    <textarea maxLength={2000} value={translation.description} rows={2} className="mt-1 w-full rounded border p-2"
                      onChange={(event) => setTranslations((items) => items.map((item) => item.language === translation.language ? { ...item, description: event.target.value } : item))} />
                  </label>
                  <label className="block text-xs">Homepage search result title
                    <input maxLength={160} value={translation.seo_title || ""} placeholder={translation.title}
                      className="mt-1 w-full rounded border p-2"
                      onChange={(event) => setTranslations((items) => items.map((item) => item.language === translation.language ? { ...item, seo_title: event.target.value } : item))} />
                  </label>
                  <label className="block text-xs">Homepage search result description
                    <textarea maxLength={320} rows={2} value={translation.seo_description || ""}
                      className="mt-1 w-full rounded border p-2"
                      onChange={(event) => setTranslations((items) => items.map((item) => item.language === translation.language ? { ...item, seo_description: event.target.value } : item))} />
                  </label>
                  {[{ id: "home", title: "Home", sections: translation.sections }, ...translation.pages].map((page, pageIndex) => (
                    <details key={page.id} className="rounded border p-2">
                      <summary className="cursor-pointer text-sm font-medium">{page.title} · {page.sections.length} sections</summary>
                      {pageIndex > 0 && (
                        <div className="mt-2 grid gap-2">
                          <label className="text-xs">Page title
                            <input maxLength={160} className="mt-1 w-full rounded border p-2" value={page.title}
                              onChange={(event) => setTranslations((items) => items.map((item) => item.language !== translation.language ? item : {
                                ...item, pages: item.pages.map((p) => p.id === page.id ? { ...p, title: event.target.value } : p),
                              }))} />
                          </label>
                          <label className="text-xs">Page description
                            <textarea maxLength={2000} rows={2} className="mt-1 w-full rounded border p-2" value={page.description || ""}
                              onChange={(event) => setTranslations((items) => items.map((item) => item.language !== translation.language ? item : {
                                ...item, pages: item.pages.map((p) => p.id === page.id ? { ...p, description: event.target.value } : p),
                              }))} />
                          </label>
                          <label className="text-xs">Search result title
                            <input maxLength={160} className="mt-1 w-full rounded border p-2" value={page.seo_title || ""}
                              placeholder={page.title}
                              onChange={(event) => setTranslations((items) => items.map((item) => item.language !== translation.language ? item : {
                                ...item, pages: item.pages.map((p) => p.id === page.id ? { ...p, seo_title: event.target.value } : p),
                              }))} />
                          </label>
                          <label className="text-xs">Search result description
                            <textarea maxLength={320} rows={2} className="mt-1 w-full rounded border p-2" value={page.seo_description || ""}
                              onChange={(event) => setTranslations((items) => items.map((item) => item.language !== translation.language ? item : {
                                ...item, pages: item.pages.map((p) => p.id === page.id ? { ...p, seo_description: event.target.value } : p),
                              }))} />
                          </label>
                          <label className="text-xs">Page URL slug
                            <input maxLength={100} className="mt-1 w-full rounded border p-2" value={page.slug}
                              onChange={(event) => setTranslations((items) => items.map((item) => item.language !== translation.language ? item : {
                                ...item, pages: item.pages.map((p) => p.id === page.id ? { ...p, slug: event.target.value } : p),
                              }))} />
                          </label>
                        </div>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2" aria-label="Add translated section">
                        {["heading", "text", "button", "image"].map((type) => (
                          <button key={type} type="button" disabled={busy || page.sections.length >= 100}
                            className="rounded border px-2 py-1 text-xs disabled:opacity-50"
                            onClick={() => addTranslatedSection(translation.language, page.id, type)}>+ {type}</button>
                        ))}
                      </div>
                      {page.sections.map((section) => (
                        <div key={section.id} className="mt-2 space-y-2 rounded border p-2">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button type="button" disabled={page.sections[0]?.id === section.id} className="text-xs disabled:opacity-40"
                              onClick={() => moveTranslatedSection(translation.language, page.id, section.id, -1)}>↑ Up</button>
                            <button type="button" disabled={page.sections[page.sections.length - 1]?.id === section.id} className="text-xs disabled:opacity-40"
                              onClick={() => moveTranslatedSection(translation.language, page.id, section.id, 1)}>↓ Down</button>
                            <button type="button" disabled={page.sections.length >= 100} className="text-xs disabled:opacity-40"
                              onClick={() => duplicateTranslatedSection(translation.language, page.id, section.id)}>Duplicate</button>
                            <button type="button" className="text-xs text-red-700"
                              onClick={() => removeTranslatedSection(translation.language, page.id, section.id)}>Remove section</button>
                          </div>
                          <label className="block text-xs capitalize">{section.type} content
                            <textarea rows={2} maxLength={3000} className="mt-1 w-full rounded border p-2" value={section.text}
                              onChange={(event) => updateTranslatedSection(translation.language, page.id, section.id, { text: event.target.value })} />
                          </label>
                          {(section.type === "button" || section.type === "image") && (
                            <label className="block text-xs">{section.type === "button" ? "Button destination URL" : "External image URL"}
                              <input type={section.type === "button" ? "text" : "url"} list={section.type === "button" ? "website-internal-page-links" : undefined}
                                maxLength={2048} value={section.url || ""} placeholder={section.type === "button" ? "https://example.com or /contact/" : "https://"}
                                className="mt-1 w-full rounded border p-2"
                                onChange={(event) => updateTranslatedSection(translation.language, page.id, section.id, { url: event.target.value, ...(section.type === "image" && event.target.value ? { asset_id: "" } : {}) })} />
                            </label>
                          )}
                          {section.type === "image" && (
                            <label className="block text-xs">Photo from your library
                              <select value={section.asset_id || ""} className="mt-1 w-full rounded border p-2"
                                onChange={(event) => updateTranslatedSection(translation.language, page.id, section.id, { asset_id: event.target.value || "", ...(event.target.value ? { url: "" } : {}) })}>
                                <option value="">No library photo selected</option>
                                {libraryImages.filter((file) => ["image/jpeg", "image/png", "image/webp"].includes(file.content_type)).map((file) => <option key={file.id} value={file.id}>{file.original_filename || file.id}</option>)}
                              </select>
                              <input type="file" accept="image/jpeg,image/png,image/webp" disabled={busy}
                                className="mt-2 block w-full text-xs" aria-label="Upload translated page photo"
                                onChange={(event) => { const file = event.target.files?.[0]; if (file) uploadTranslatedImage(file, translation.language, page.id, section.id); event.target.value = ""; }} />
                            </label>
                          )}
                        </div>
                      ))}
                    </details>
                  ))}
                </div>
              ))}
            </section>
            <section className="space-y-3 rounded-lg border p-4" aria-label="Template library">
              <h3 className="font-semibold">Website template library</h3>
              <p className="text-sm">25 industries × 10 editable page compositions. 100 base templates; 250 total with the full library. Three legacy layouts are retained separately.</p>
              {catalog && <p role="status" className="text-sm">{catalog.full_library ? (catalog.access_reason === "enterprise" ? "All 250 included with Enterprise — no template surcharge." : "All 250 unlocked by your template subscription.") : "100 base templates available. The full library adds 150 templates."}</p>}
              {catalogError && <p role="alert" className="text-sm text-red-700">{catalogError}</p>}
              <button type="button" onClick={loadCatalog} disabled={busy} className="text-sm underline">Refresh template access</button>
              <label className="block text-sm">Search templates
                <input value={templateSearch} onChange={(e) => setTemplateSearch(e.target.value)} className="mt-1 block w-full rounded border p-2" />
              </label>
              <label className="block text-sm">Industry
                <select value={templateCategory} onChange={(e) => setTemplateCategory(e.target.value)} className="mt-1 block w-full rounded border p-2">
                  <option value="">All industries</option>
                  {[...new Set(TEMPLATES.map((item) => item.category).filter(Boolean))].map((category) => <option key={category}>{category}</option>)}
                </select>
              </label>
              {catalog && !catalog.full_library && <div>
                <button type="button" disabled={busy || !catalog.checkout_configured || !catalog.can_manage} onClick={upgradeTemplates} className="rounded-lg bg-[#202B60] px-4 py-2 text-white disabled:opacity-50">Full library · €4.99/month</button>
                {!catalog.checkout_configured && <p className="mt-2 text-sm">Upgrade checkout is awaiting payment configuration. The 100 base templates remain available.</p>}
                {!catalog.can_manage && <p className="mt-2 text-sm">Ask your workspace owner to manage this upgrade.</p>}
              </div>}
              {catalog?.access_reason === "addon" && catalog.can_manage && <button type="button" disabled={busy} onClick={cancelTemplateUpgrade} className="text-sm underline">Cancel template add-on at period end</button>}
            </section>
            <label className="block text-sm font-medium">Template
              <select value={template} disabled={busy} onChange={(e) => setTemplate(e.target.value)}
                className="mt-1 block w-full rounded-md border p-2">
                {!TEMPLATES.some((item) => item.id === template) && (
                  <option value={template} disabled>Unavailable template ({template}) — choose another</option>
                )}
                {!filteredTemplates.some((item) => item.id === template) && TEMPLATES.some((item) => item.id === template) && <option value={template}>{TEMPLATES.find((item) => item.id === template).name} (current)</option>}
                {filteredTemplates.map((t) => <option key={t.id} value={t.id} disabled={t.available === false}>{t.name}{t.available === false ? " · Full library required" : ""}</option>)}
              </select>
            </label>
            <button type="button" onClick={addStarterLayout} disabled={busy || !catalog || !activeTemplate.available}
              className="rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-50">
              Apply starter page layout
            </button>
            <p className="text-xs text-slate-500">Adds editable sample sections to the currently selected page. Existing sections require confirmation to replace.</p>
            {starterLayoutApplied && <p role="status" className="text-xs text-amber-700">Sample content is for demonstration only. Replace placeholder text, images and contact links before publishing.</p>}
            <div aria-label="Template color preview" className="flex items-center gap-3 rounded-lg border p-3">
              <span className="h-8 w-8 shrink-0 rounded-full border" style={{ backgroundColor: activeTemplate.accent }} />
              <span className="h-8 w-8 shrink-0 rounded-full border" style={{ backgroundColor: activeTemplate.surface }} />
              <span className="text-sm text-slate-600">{activeTemplate.name} · Theme preview</span>
            </div>
            <label className="block text-sm font-medium">Business description
              <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                maxLength={2000} rows={4} className="mt-1 block w-full rounded-md border p-2" />
            </label>
            <div className="flex flex-wrap gap-3">
              {selected
                ? <button disabled={busy} onClick={save} className="rounded-lg bg-slate-900 px-4 py-2 text-white disabled:opacity-50">Save changes</button>
                : <button disabled={busy} onClick={create} className="rounded-lg bg-slate-900 px-4 py-2 text-white disabled:opacity-50">Create draft</button>}
              {selected && <button type="button" disabled={busy || hasUnsavedChanges} onClick={downloadPublication}
                className="rounded-lg border px-4 py-2 disabled:opacity-50">
                {downloading ? "Preparing download…" : "Download website package"}
              </button>}
              {selected && <button disabled={busy} onClick={remove} className="rounded-lg border px-4 py-2 text-red-700">Delete draft</button>}
            </div>
            {selected && <p className="text-xs text-slate-500">Download prepares the saved website version shown in this editor for hosting. It does not publish it. Save changes first; external images remain linked to their original URL.</p>}
            {selected && <PrivateWebsiteReleases key={selected.id}
              draftId={selected.id} sourceVersion={selected.updated_at}
              hasUnsavedChanges={hasUnsavedChanges} busy={busy} setBusy={setBusy} />}
          </section>
          <section className="rounded-xl border bg-white p-5 space-y-3">
            <h2 className="font-semibold">Website pages</h2>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setActivePage(null)}
                className={`rounded border px-3 py-2 text-sm ${!activePage ? "bg-slate-900 text-white" : ""}`}>Home</button>
              {pages.map((page) => (
                <button key={page.id} type="button" onClick={() => setActivePage(page.id)}
                  className={`rounded border px-3 py-2 text-sm ${activePage === page.id ? "bg-slate-900 text-white" : ""}`}>
                  {page.title}
                </button>
              ))}
              <button type="button" disabled={pages.length >= 30} onClick={addPage}
                className="rounded border px-3 py-2 text-sm">+ Add page</button>
            </div>
            {activePage && pages.find((page) => page.id === activePage) && (
              <div className="grid gap-2 sm:grid-cols-2">
                <input aria-label="Page title" maxLength={160} className="rounded border p-2"
                  value={pages.find((page) => page.id === activePage).title}
                  onChange={(e) => updatePage(activePage, { title: e.target.value })} />
                <input aria-label="Page URL slug" maxLength={100} className="rounded border p-2"
                  value={pages.find((page) => page.id === activePage).slug}
                  onChange={(e) => updatePage(activePage, { slug: e.target.value })} />
                <label className="block text-xs sm:col-span-2">Page description
                  <textarea maxLength={2000} rows={2} className="mt-1 w-full rounded border p-2"
                    value={pages.find((page) => page.id === activePage).description || ""}
                    onChange={(e) => updatePage(activePage, { description: e.target.value })} />
                </label>
                <div className="space-y-2 rounded border p-3 sm:col-span-2" aria-label="Page SEO settings">
                  <p className="text-sm font-medium">Search result settings</p>
                  <p className="text-xs text-slate-500">Saved with this draft for publication. These fields do not change the visible page title or description.</p>
                  <label className="block text-xs">Search result title
                    <input maxLength={160} className="mt-1 w-full rounded border p-2"
                      value={pages.find((page) => page.id === activePage).seo_title || ""}
                      placeholder={pages.find((page) => page.id === activePage).title}
                      onChange={(e) => updatePage(activePage, { seo_title: e.target.value })} />
                  </label>
                  <label className="block text-xs">Search result description
                    <textarea maxLength={320} rows={2} className="mt-1 w-full rounded border p-2"
                      value={pages.find((page) => page.id === activePage).seo_description || ""}
                      onChange={(e) => updatePage(activePage, { seo_description: e.target.value })} />
                  </label>
                </div>
                <button type="button" disabled={pages.length >= 30} className="text-left text-sm disabled:opacity-50"
                  onClick={() => duplicatePage(activePage)}>Duplicate page</button>
                <button type="button" className="text-left text-sm text-red-700"
                  onClick={() => removePage(activePage)}>Remove page</button>
              </div>
            )}
          </section>
          <section className="rounded-xl border bg-white p-5 space-y-3">
            <h2 className="font-semibold">Page sections</h2>
            <p className="text-xs text-slate-500">Buttons can link to a website page, https:// address, mailto:email@example.com or tel:+34123456789.</p>
            <div className="flex flex-wrap gap-2">
              {["heading", "text", "button", "image"].map((type) => (
                <button key={type} type="button" disabled={visibleSections.length >= 100}
                  onClick={() => addSection(type)} className="rounded border px-3 py-2 text-sm">
                  Add {type}
                </button>
              ))}
            </div>
            {visibleSections.map((section, index) => (
              <div key={section.id} className="space-y-2 rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <strong className="capitalize text-sm">{section.type}</strong>
                  <div className="flex gap-3 text-sm">
                    <button type="button" disabled={index === 0} onClick={() => moveSection(index, -1)}>Up</button>
                    <button type="button" disabled={index === visibleSections.length - 1} onClick={() => moveSection(index, 1)}>Down</button>
                    <button type="button" disabled={visibleSections.length >= 100} onClick={() => duplicateSection(section.id)} className="disabled:opacity-50">Duplicate</button>
                    <button type="button" onClick={() => removeSection(section.id)} className="text-red-700">Remove</button>
                  </div>
                </div>
                <textarea aria-label={section.type + " text"} maxLength={3000} rows={2}
                  value={section.text} onChange={(e) => editSection(section.id, { text: e.target.value })}
                  placeholder={section.type === "image" ? "Image description" : "Enter text"}
                  className="w-full rounded border p-2 text-sm" />
                {section.type === "image" && (
                  <div className="space-y-2">
                    <label className="block text-xs">Select a company photo
                      <select value={section.asset_id || ""}
                        onChange={(e) => editSection(section.id, { asset_id: e.target.value, url: "" })}
                        className="mt-1 w-full rounded border p-2">
                        <option value="">No library photo</option>
                        {libraryImages.filter((file) => ["image/jpeg", "image/png", "image/webp"].includes(file.content_type)).map((file) =>
                          <option key={file.id} value={file.id}>{file.original_filename}</option>)}
                      </select>
                    </label>
                    <label className="block text-xs">Upload your own photo
                      <input type="file" accept="image/png,image/jpeg,image/webp" disabled={busy}
                        onChange={(e) => { uploadImage(e.target.files?.[0], section.id); e.target.value = ""; }}
                        className="block w-full" />
                    </label>
                  </div>
                )}
                {(section.type === "button" || section.type === "image") && (
                  <input type={section.type === "button" ? "text" : "url"} list={section.type === "button" ? "website-internal-page-links" : undefined}
                    maxLength={2048} aria-label={section.type + " URL"}
                    value={section.url} onChange={(e) => editSection(section.id, { url: e.target.value, ...(section.type === "image" && e.target.value ? { asset_id: "" } : {}) })}
                    placeholder={section.type === "button" ? "https://example.com or /contact/" : "https://example.com"} className="w-full rounded border p-2 text-sm" />
                )}
              </div>
            ))}
          </section>
          <section className="rounded-xl border bg-white p-5 space-y-2" aria-label="Website readiness checklist">
            <h2 className="font-semibold">Website readiness {uniqueContentWarnings.length ? `· ${uniqueContentWarnings.length} item${uniqueContentWarnings.length === 1 ? "" : "s"} to review` : "· No missing fields detected"}</h2>
            <p className="text-sm text-slate-600">Content checks only. Publishing is not available yet.</p>
            {uniqueContentWarnings.length === 0 ? <p className="text-sm text-green-700">No missing content detected. Review all text and links before publishing.</p> : (
              <ul className="list-disc space-y-1 pl-5 text-sm text-amber-800">
                {uniqueContentWarnings.map((warning) => <li key={warning}>{warning}</li>)}
              </ul>
            )}
            {pages.some((page) => !page.sections.length) && (
              <div className="flex flex-wrap gap-2" aria-label="Pages needing content">
                {pages.filter((page) => !page.sections.length).map((page) => (
                  <button key={page.id} type="button" onClick={() => setActivePage(page.id)}
                    className="rounded border border-amber-300 px-2 py-1 text-xs text-amber-900">
                    Edit {page.title || page.slug || "untitled page"}
                  </button>
                ))}
              </div>
            )}
          </section>
          <section className="space-y-2 rounded-xl border bg-white p-5" aria-label="Search result preview">
            <h2 className="font-semibold">Search result preview</h2>
            <p className="text-xs text-slate-500">Draft example for the selected preview page and language. Not published; actual search results may differ.</p>
            <div className="max-w-2xl break-words rounded border bg-slate-50 p-4"
              lang={previewTranslation?.language || defaultLanguage}>
              <p className="text-xs text-slate-600">{previewSeoPath}</p>
              <p className="mt-1 text-xl text-blue-800">{previewSeoTitle}</p>
              {previewSeoDescription
                ? <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{previewSeoDescription}</p>
                : <p className="mt-1 text-sm text-amber-800">Add a search result description for this page and language.</p>}
            </div>
          </section>
          <section className="overflow-hidden rounded-xl border bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2 text-xs text-slate-500">
              <span>Preview only · Not published</span>
              <div className="flex flex-wrap items-center gap-2">
                <label htmlFor="website-preview-language">Preview language</label>
                <select id="website-preview-language" className="rounded border px-2 py-1"
                  value={previewLanguage === "primary" || translations.some((item) => item.language === previewLanguage) ? previewLanguage : "primary"}
                  onChange={(event) => { setPreviewLanguage(event.target.value); setMobileMenuOpen(false); }}>
                  <option value="primary">{SITE_LANGUAGES.find((item) => item.code === defaultLanguage)?.name || defaultLanguage} (primary)</option>
                  {translations.map((item) => <option key={item.language} value={item.language}>
                    {SITE_LANGUAGES.find((language) => language.code === item.language)?.name || item.language}
                  </option>)}
                </select>
              </div>
              <div role="group" aria-label="Preview screen size" className="flex gap-2">
                <button type="button" aria-pressed={showPreview} onClick={() => setShowPreview((value) => !value)} className="rounded border px-3 py-1">{showPreview ? "Hide preview" : "Show preview"}</button>
                <button type="button" aria-pressed={previewLinks} onClick={() => setPreviewLinks((value) => !value)}
                  className={previewLinks ? "rounded bg-slate-900 px-3 py-1 text-white" : "rounded border px-3 py-1"}>Test links</button>
                <button type="button" aria-pressed={previewDevice === "desktop"}
                  onClick={() => { setPreviewDevice("desktop"); setMobileMenuOpen(false); }}
                  className={previewDevice === "desktop" ? "rounded bg-slate-900 px-3 py-1 text-white" : "rounded border px-3 py-1"}>Desktop</button>
                <button type="button" aria-pressed={previewDevice === "tablet"}
                  onClick={() => { setPreviewDevice("tablet"); setMobileMenuOpen(false); }}
                  className={previewDevice === "tablet" ? "rounded bg-slate-900 px-3 py-1 text-white" : "rounded border px-3 py-1"}>Tablet</button>
                <button type="button" aria-pressed={previewDevice === "mobile"}
                  onClick={() => { setPreviewDevice("mobile"); setMobileMenuOpen(false); }}
                  className={previewDevice === "mobile" ? "rounded bg-slate-900 px-3 py-1 text-white" : "rounded border px-3 py-1"}>Mobile</button>
              </div>
            </div>
            {showPreview && <div className={previewDevice === "mobile" ? "mx-auto max-w-[390px] border-x shadow-sm" : previewDevice === "tablet" ? "mx-auto max-w-[768px] border-x shadow-sm" : "w-full"}>
            {previewDevice === "mobile" && (
              <button type="button" aria-expanded={mobileMenuOpen} aria-controls="website-preview-nav"
                onClick={() => setMobileMenuOpen((open) => !open)}
                className="w-full border-b bg-white px-4 py-3 text-left text-sm font-semibold">
                {mobileMenuOpen ? `${previewMenuLabels[1]} ×` : `${previewMenuLabels[0]} ☰`}
              </button>
            )}
            <nav id="website-preview-nav" aria-label="Website preview navigation"
              className={previewDevice === "mobile" && !mobileMenuOpen ? "hidden" : "flex flex-wrap gap-2 border-b bg-white p-3"}>
              <button type="button" onClick={() => { setActivePage(null); setMobileMenuOpen(false); }}
                aria-current={!activePage ? "page" : undefined}
                className={!activePage ? "rounded bg-slate-900 px-3 py-1 text-white" : "rounded px-3 py-1 text-slate-700"}>{previewHomeLabel}</button>
              {previewNavigationPages.map((page, index) => (
                <button type="button" key={page.id} onClick={() => { setActivePage(pages[index]?.id || null); setMobileMenuOpen(false); }}
                  aria-current={activePage === pages[index]?.id ? "page" : undefined}
                  className={activePage === pages[index]?.id ? "rounded bg-slate-900 px-3 py-1 text-white" : "rounded px-3 py-1 text-slate-700"}>
                  {page.title || previewUntitledLabel}
                </button>
              ))}
            </nav>
            <div className={`template-page break-words px-4 py-10 sm:px-8 sm:py-14 ${activeTemplate.layout === "studio" ? "text-left" : "text-center"}`} style={{ backgroundColor: activeTemplate.surface, color: activeTemplate.text }}>
              <style>{activeTemplate.css || ""}</style>
              <h2 className={`text-2xl sm:text-3xl ${activeTemplate.layout === "studio" ? "font-light tracking-wide" : "font-bold"}`} style={{ color: activeTemplate.accent }}>{previewTitle}</h2>
              <p className="mx-auto mt-3 max-w-xl">{previewPage ? (previewPageDescription || (previewContent.length ? "" : previewPlaceholders.emptyPage)) : (previewDescription || previewPlaceholders.description)}</p>
              <div className={`template-sections mt-8 max-w-xl space-y-5 ${activeTemplate.layout === "studio" ? "" : "mx-auto"}`}>
                {(activeTemplate.legacy === false ? groupTemplateSections(previewContent) : [previewContent]).map((group, index) => (
                  <div key={index} className={activeTemplate.legacy === false ? "template-block" : "space-y-5"}>
                  {group.map((section) => {
                  const url = section.url && !isInvalidUrl(section.url, section.type === "button") ? section.url : "";
                  if (section.type === "heading") return <h3 key={section.id} className="text-2xl font-bold">{section.text || previewPlaceholders.heading}</h3>;
                  if (section.type === "text") return <p key={section.id} className="whitespace-pre-wrap">{section.text || previewPlaceholders.text}</p>;
                  if (section.type === "button" && previewLinks && url.startsWith("/")) return (
                    <button key={section.id} type="button" onClick={() => followInternalLink(url)}
                      className={`inline-block px-5 py-2 text-white ${activeTemplate.layout === "studio" ? "rounded-none" : "rounded-lg"}`}
                      style={{ backgroundColor: activeTemplate.accent }}>{section.text || previewPlaceholders.button}</button>
                  );
                  if (section.type === "button") return previewLinks && url ? <a key={section.id} href={url} target={/^https?:/i.test(url) ? "_blank" : undefined} rel="noopener noreferrer" className={`inline-block px-5 py-2 text-white ${activeTemplate.layout === "studio" ? "rounded-none" : "rounded-lg"}`} style={{ backgroundColor: activeTemplate.accent }}>{section.text || previewPlaceholders.button}{/^https?:/i.test(url) ? " ↗" : ""}</a> : <span key={section.id} className={`inline-block px-5 py-2 text-white ${activeTemplate.layout === "studio" ? "rounded-none" : "rounded-lg"}`} style={{ backgroundColor: activeTemplate.accent }}>{section.text || previewPlaceholders.button}{/^https?:/i.test(url) ? " ↗" : ""}</span>;
                  if (section.type === "image" && section.asset_id) return <PrivateImage key={section.id} assetId={section.asset_id} alt={section.text} />;
                  if (section.type === "image") return url ? <ExternalImage key={`${section.id}:${url}`} url={url} alt={section.text} /> : <div key={section.id} className="rounded border border-white p-6">{previewPlaceholders.image}</div>;
                  return null;
                })}</div>))}
              </div>
            </div>
            <footer className="border-t px-4 py-3 text-center text-xs" style={{ backgroundColor: activeTemplate.surface, color: activeTemplate.text }}>
              <a href="https://zynthoro.ai" target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 hover:underline">Built with Zynthoro.ai</a>
            </footer>
            </div>}
          </section>
        </div>
      </fieldset>
    </div>
  );
}

